import moment from "moment";
import checkRruleIfSameDay from "@/utils/checkRruleIfSameDay";

const toMinutes = (hms) => {
  if (!hms) return 0;
  const [h, m] = hms.split(":").map(Number);
  return h * 60 + m;
};

const fromMinutes = (mins) => {
  const h = Math.floor(mins / 60)
    .toString()
    .padStart(2, "0");
  const m = (mins % 60).toString().padStart(2, "0");
  return `${h}:${m}:00`;
};

const HHMM = (s) => (s && s.length === 5 ? `${s}:00` : s);

const subtract = (slots, leaveStart, leaveEnd) => {
  const ls = toMinutes(leaveStart);
  const le = toMinutes(leaveEnd);
  const out = [];
  for (const slot of slots) {
    const ss = toMinutes(slot.start);
    const se = toMinutes(slot.end);
    if (le <= ss || ls >= se) {
      out.push(slot);
      continue;
    }
    if (ls > ss)
      out.push({ start: slot.start, end: fromMinutes(ls), roomId: slot.roomId });
    if (le < se)
      out.push({ start: fromMinutes(le), end: slot.end, roomId: slot.roomId });
  }
  return out;
};

const merge = (slots) => {
  if (slots.length === 0) return slots;
  const sorted = [...slots].sort(
    (a, b) => toMinutes(a.start) - toMinutes(b.start)
  );
  const out = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const prev = out[out.length - 1];
    const cur = sorted[i];
    if (prev.roomId === cur.roomId && prev.end === cur.start) {
      out[out.length - 1] = { ...prev, end: cur.end };
    } else {
      out.push(cur);
    }
  }
  return out;
};

/**
 * Apply a single proposed override to already-resolved slots — used only for the
 * side-panel LIVE PREVIEW while a form is open (the backend resolver owns the
 * persisted schedule). leave = subtract a range; shift = add (and merge).
 * @param {Array<{start,end,roomId}>} slots
 * @param {{type:'leave'|'shift', from:string, to:string, roomId?:number|null}} pending
 */
export const applyLocalOverride = (slots, pending) => {
  if (!pending) return slots;
  const fromHms = HHMM(pending.from);
  const toHms = HHMM(pending.to);
  if (pending.type === "leave") {
    return subtract(slots, fromHms, toHms);
  }
  if (pending.type === "shift") {
    return merge([
      ...slots,
      { start: fromHms, end: toHms, roomId: pending.roomId ?? null },
    ]);
  }
  return slots;
};

/**
 * The recurring rules whose recurrence lands on a given date — for the day
 * panel's editable "ตารางประจำ" rows. Uses the full-rrule helper so weekly
 * (BYDAY), monthly-ordinal (BYSETPOS) and UNTIL-bounded rules all resolve
 * correctly (a plain day-of-week check would mis-handle BYSETPOS/UNTIL).
 *
 * @param {Array<{id,rruleStr,startTime,endTime,roomId}>} rules
 * @param {string} date "YYYY-MM-DD"
 * @returns {Array<{ruleId,rruleStr,start,end,roomId}>}
 */
export const rulesActiveOnDate = (rules, date) => {
  const dateObj = moment(date);
  return (rules || [])
    .filter(
      (r) => r.rruleStr && checkRruleIfSameDay({ dateObj, rruleStr: r.rruleStr })
    )
    .map((r) => ({
      ruleId: r.id,
      rruleStr: r.rruleStr,
      start: HHMM(r.startTime),
      end: HHMM(r.endTime),
      roomId: r.roomId ?? null,
    }));
};
